// Spoken Notification Player
//
// Plays the Console Chat Agent's *proactive spoken notifications* in the page
// context. A service worker cannot play audio itself, so when a push arrives the
// SW relays the spoken content to open pages via a `SPEAK_NOTIFICATION` message
// (see app/javascript/service-worker.js). This module receives that message and
// performs the actual background audio playback: it either plays a pre-supplied
// audio URL or synthesizes the text through the same secure ElevenLabs proxy the
// Console Agent uses (`POST /api/v1/tts/token`), so ELEVENLABS_API_KEY never
// reaches the browser.
//
// Imported globally from application.js so it is listening on every page,
// regardless of which route the user is on when the push arrives.

const SPOKEN_ENABLED_KEY = 'allspark:spoken_notifications_enabled'

export function isSpokenNotificationEnabled(): boolean {
  try {
    const val = localStorage.getItem(SPOKEN_ENABLED_KEY)
    return val === null ? true : val === 'true'
  } catch {
    return true
  }
}

export function setSpokenNotificationEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SPOKEN_ENABLED_KEY, String(enabled))
  } catch {
    /* ignore — storage may be unavailable in private mode */
  }
}

function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

// The <audio> element we're currently speaking through. A new notification
// interrupts any still-playing one so spoken notifications never overlap.
let currentAudio: HTMLAudioElement | null = null

// Resolve a playable audio URL: prefer a pre-synthesized URL, otherwise ask the
// server-side TTS proxy to synthesize the text. Returns null on any failure —
// spoken notifications are best-effort and the visible notification still
// conveys the message.
async function resolveAudioUrl(text?: string, ttsUrl?: string): Promise<string | null> {
  if (ttsUrl) return ttsUrl

  const speakText = (text || '').trim()
  if (!speakText) return null

  try {
    const response = await fetch('/api/v1/tts/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': getCsrfToken()
      },
      credentials: 'same-origin',
      body: JSON.stringify({ text: speakText })
    })

    if (!response.ok) {
      console.warn(`[SpokenNotification] TTS synthesis failed (HTTP ${response.status})`)
      return null
    }

    const data = await response.json()
    return (data && data.url) || null
  } catch (error) {
    console.error('[SpokenNotification] TTS request failed:', error)
    return null
  }
}

function play(url: string): void {
  // Stop and release any in-progress speech first.
  if (currentAudio) {
    currentAudio.pause()
    currentAudio.src = ''
    currentAudio = null
  }

  const audio = new Audio()
  audio.src = url
  currentAudio = audio

  audio.addEventListener('ended', () => {
    if (currentAudio === audio) currentAudio = null
  })

  const playback = audio.play()
  if (playback && typeof playback.catch === 'function') {
    playback.catch((error) => {
      // Browsers block audio that isn't tied to a recent user gesture. A push
      // that lands while the tab is backgrounded commonly hits this; the visible
      // notification still informs the user, so we log and move on. Playback
      // triggered by a notification click (a fresh gesture) is generally allowed.
      console.warn('[SpokenNotification] audio playback blocked:', error)
    })
  }
}

export async function speakNotification(opts: { text?: string; ttsUrl?: string }): Promise<void> {
  if (!isSpokenNotificationEnabled()) return
  const url = await resolveAudioUrl(opts.text, opts.ttsUrl)
  if (url) play(url)
}

// Listen for spoken-notification relays from the service worker.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data
    if (!data || data.type !== 'SPEAK_NOTIFICATION') return

    // Avoid double-speaking: when the push lands while this tab is the active,
    // visible tab, the Console Agent may already be voicing the reply live over
    // its realtime channel. Only auto-speak from a background push (hidden tab)
    // or when the user explicitly opened the notification (viaClick).
    const fromClick = data.viaClick === true
    if (!fromClick && document.visibilityState === 'visible') return

    speakNotification({ text: data.text, ttsUrl: data.ttsUrl })
  })
}
